import {
  GameObject,
  Quat,
  TimerGameObject,
  Vector3,
  quatToVector2D,
  vector2Multiply,
  vector3Subtract,
} from "realms-engine";
import { FloatingWeaponGameObject } from "./floatingWeapon.gameObject";
import {
  CreatureState,
  CreatureStateType,
  PlayerState,
  WeaponConfig,
  WeaponType,
} from "mmo-common";
import { toThreeQuaternion } from "realms-engine-browser";
import THREE from "realms-three";

const SHOW_FLOATING_WEAPON_WAIT_TIME = 3000;

interface PlayerFloatingWeaponEventMap {
  onShowChange: boolean;
}

export class PlayerFloatingWeapon extends GameObject<PlayerFloatingWeaponEventMap> {
  private floatingWeapon: FloatingWeaponGameObject;
  private showFloatingWeaponTimer: TimerGameObject;
  private showingFloatingWeapon: boolean;

  constructor({ playerState }: { playerState: PlayerState }) {
    super();

    this.floatingWeapon = this.addChild(
      new FloatingWeaponGameObject({
        weapon: playerState.character.weapon,
      })
    );

    const shouldShowFloatingWeapon = this.shouldShowFloatingWeapon(
      playerState.creature.state
    );

    this.showingFloatingWeapon = shouldShowFloatingWeapon;
    this.showFloatingWeapon(shouldShowFloatingWeapon);

    this.showFloatingWeaponTimer = this.addChild(new TimerGameObject());
    this.showFloatingWeaponTimer.addListener(
      "onFinish",
      this.onShowFloatingWeaponTimerEnd
    );
  }

  setWeapon<T extends WeaponType>(weapon: WeaponConfig<T> | null) {
    this.floatingWeapon.setWeapon(weapon);
  }

  /**
   * Set whether the floating weapon is shown or not
   */
  showFloatingWeapon(show: boolean) {
    // No change so do nothing
    if (show === this.showingFloatingWeapon) {
      return;
    }

    const DELAY = show ? 800 : 0;

    this.floatingWeapon.setWeaponVisibility(show, { delay: DELAY });
    this.showingFloatingWeapon = show;
    this.dispatch("onShowChange", show);
  }

  isShowing() {
    return this.showingFloatingWeapon;
  }

  /**
   * Update the weapon position
   */
  updateFloatingWeaponPosition(targetState: {
    quaternion: Quat;
    position: Vector3;
  }) {
    const weaponOffset = 2;
    const weaponDisplacement = vector2Multiply(
      quatToVector2D(targetState.quaternion),
      weaponOffset
    );
    const target = vector3Subtract(targetState.position, {
      x: weaponDisplacement.x,
      y: 0,
      z: weaponDisplacement.y,
    });
    this.floatingWeapon.setTarget(target);
    const weaponQuat = toThreeQuaternion(targetState.quaternion);
    weaponQuat.multiply(
      new THREE.Quaternion().setFromAxisAngle(
        new THREE.Vector3(0, 0, 1),
        Math.PI / 8
      )
    );
    weaponQuat.multiply(
      new THREE.Quaternion().setFromAxisAngle(
        new THREE.Vector3(1, 0, 0),
        -Math.PI / 10
      )
    );
    this.floatingWeapon.setQuaternion(weaponQuat);
  }

  /**
   * Update whether the floating weapon should show or not base on player state
   */
  updateFloatingWeaponState(state: PlayerState) {
    const shouldShowFloatingWeapon = this.shouldShowFloatingWeapon(
      state.creature.state
    );

    if (shouldShowFloatingWeapon && !this.showFloatingWeaponTimer.isRunning()) {
      this.showFloatingWeaponTimer.start(SHOW_FLOATING_WEAPON_WAIT_TIME);
    }

    // Hide showing floating weapon
    if (!shouldShowFloatingWeapon) {
      this.showFloatingWeaponTimer.stop();
      this.showFloatingWeapon(false);
    }
  }

  private onShowFloatingWeaponTimerEnd = () => {
    this.showFloatingWeapon(true);
  };

  private shouldShowFloatingWeapon(state: CreatureState) {
    return !([CreatureStateType.NormalAttack] as string[]).includes(state.type);
  }
}
